import { useState, useEffect } from 'react';
import { IconAlertTriangleFilled } from '@tabler/icons-react';

const FONT = '"Public Sans", sans-serif';

const inputClass =
  'w-full outline-none bg-white/[0.04] focus:bg-white/[0.07] focus:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] placeholder:text-white/30 transition-all duration-200 ease-out';

export default function ModalEliminarGasto({ isOpen, onClose, onConfirm, itemCount = 1, codes = [] }) {
  const [authCode, setAuthCode] = useState('');

  useEffect(() => {
    if (isOpen) setAuthCode('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (authCode.trim()) {
      onConfirm(authCode);
      setAuthCode('');
    }
  };

  const handleClose = () => {
    setAuthCode('');
    onClose();
  };

  const plural = itemCount !== 1;
  const warning = plural
    ? `Estos ${itemCount} gastos se eliminarán permanentemente`
    : 'Este gasto se eliminará permanentemente';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={handleClose}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="eliminar-gasto-titulo"
        aria-describedby="eliminar-gasto-descripcion"
        className="flex flex-col overflow-hidden w-full"
        style={{
          backgroundColor: '#000',
          borderRadius: '28px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          maxWidth: '460px',
          maxHeight: 'min(680px, calc(100dvh - 24px))',
          boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-white/[0.06]">
          <h2
            id="eliminar-gasto-titulo"
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            {plural ? 'Eliminar gastos' : 'Eliminar gasto'}
          </h2>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Esta acción no se puede deshacer
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-5 sm:px-6 py-5">
          <div
            className="flex items-center gap-3 rounded-[20px] p-4"
            style={{ backgroundColor: 'rgba(248, 124, 43, 0.08)' }}
          >
            <span
              className="flex items-center justify-center shrink-0 w-10 h-10 rounded-full"
              style={{ backgroundColor: 'rgba(248, 124, 43, 0.14)' }}
            >
              <IconAlertTriangleFilled size={19} color="#f87c2b" />
            </span>
            <span
              id="eliminar-gasto-descripcion"
              className="min-w-0"
              style={{
                fontSize: '15px',
                lineHeight: '21px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'rgba(255, 255, 255, 0.92)',
                fontFamily: FONT,
              }}
            >
              {warning}
            </span>
          </div>

          {codes.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-5">
              {codes.map((code) => (
                <span
                  key={code}
                  className="inline-flex items-center justify-center"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    borderRadius: '9999px',
                    height: '30px',
                    padding: '0 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    fontFamily: FONT,
                    color: 'rgba(255, 255, 255, 0.75)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  Gasto #{code}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-2 mt-5">
            <label
              htmlFor="gasto-clave-autorizacion"
              style={{
                fontSize: '13px',
                lineHeight: '18px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.45)',
                fontFamily: FONT,
              }}
            >
              Clave de autorización
            </label>
            <input
              id="gasto-clave-autorizacion"
              type="password"
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
              placeholder="Ingresa la clave"
              autoComplete="current-password"
              className={inputClass}
              style={{
                border: 'none',
                borderRadius: '16px',
                height: '48px',
                padding: '0 16px',
                fontSize: '15px',
                fontWeight: 500,
                fontFamily: FONT,
                color: 'rgba(255, 255, 255, 0.95)',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 shrink-0 px-5 sm:px-6 py-4 border-t border-white/[0.06]">
          <button
            type="button"
            onClick={handleClose}
            className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.1]"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: 'rgba(255, 255, 255, 0.9)',
              fontFamily: FONT,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!authCode.trim()}
            className="flex items-center justify-center h-12 px-7 rounded-full border-none touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:brightness-110"
            style={{
              backgroundColor: authCode.trim() ? '#ff453a' : 'rgba(255, 255, 255, 0.06)',
              fontSize: '15px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: authCode.trim() ? '#fff' : 'rgba(255, 255, 255, 0.35)',
              fontFamily: FONT,
              cursor: authCode.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}
